import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CheckoutConfirmationPage() {
  const { style } = useStyle();


// ...

React.useEffect(() => {
  // prevent duplicate firing in the same tab/session
  const alreadyTracked = sessionStorage.getItem("pa_purchase_tracked");
  if (alreadyTracked === "true") return;

  // gtag may not exist yet if Google hasn't initialized
  if (!window.gtag) 
    {
      console.log("no google tag")
      return
    };

    console.log("sending purchase", {
      transaction_id: sessionStorage.getItem("transaction_id"),
      currency: sessionStorage.getItem("currency"),
      value: sessionStorage.getItem("order_total"),
      gtagExists: !!window.gtag,
    });


  window.gtag("event", "purchase", {
    transaction_id: sessionStorage.getItem('transaction_id'),
    currency: sessionStorage.getItem('currency'),
    value: sessionStorage.getItem('order_total'),
  });
}, []);

React.useEffect(() => {
  window.scrollTo({ top: 0, behavior: "auto" });
}, []);

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";
  const panelBg2 = style.darkerPanel || "rgba(6, 9, 14, 0.55)";

  const steps = [
    {
      icon: <DownloadRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "Download your plugin",
      text: 'Go to your My Gear page. Choose the download option for your operating system (Windows or Mac), this will download the installer program.',
    },
    {
      icon: <LoginRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "Install and sign in",
      text: "Be sure that your DAW is closed, and then run the installer program that was downloaded. Once it is finished, open your DAW and load the new plugin. Use your Parish Audio account email address and password to unlock it and sign in.",
    },
    {
      icon: <AppsRoundedIcon sx={{ color: style.accentGreen || style.color4 }} />,
      title: "Start creating",
      text: "That's it! You are ready to start creating.",
    },
  ];

  return (
    <>
      <Seo
        title="Order Confirmed | Parish Audio"
        description="Your Parish Audio order is confirmed and your download is ready."
        canonical="https://parishaudio.com/checkout-confirmation"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "clip",
          px: { xs: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 980 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              pt: 2,
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: 3,
                mt: 1,
                width: "95%",
                margin: "auto",
                minHeight: { xs: 120, sm: 145, md: 150 },
                display: "flex",
                alignItems: "center",
                backgroundColor: "black",
                backgroundImage: `
                  linear-gradient(
                    90deg,
                    rgba(8, 12, 18, 0.80) 0%,
                    rgba(8, 12, 18, 0.72) 40%,
                    rgba(8, 12, 18, 0.50) 72%,
                    rgba(8, 12, 18, 0.34) 100%
                  ),
                  url('/assets/HeroBanner1.png')
                `,
                backgroundSize: "cover",
                backgroundPosition: "center center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.42) 100%)",
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  px: { xs: 2, md: 4 },
                  py: { xs: 1, md: 1.5 },
                }}
              >
                <Stack spacing={2} alignItems="center" textAlign="center">

                  <Typography
                    sx={{
                      color: "white",
                      fontSize: { xs: "1.65rem", md: "2.2rem" },
                      fontWeight: 800,
                      lineHeight: 1.08,
                      letterSpacing: "-0.03em",
                      maxWidth: 700,
                    }}
                  >
                    Thank you!
                  </Typography>

                </Stack>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2, md: 3 } }}>
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.25 },
                    borderRadius: "14px",
                    backgroundColor: panelBg,
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        sx={{
                          color: style.color4,
                          fontSize: { xs: "1.18rem", md: "1.34rem" },
                          fontWeight: 800,
                          mb: 0.5,
                        }}
                      >
                        You’re all set!
                      </Typography>
                      <Typography
                    sx={{
                      color: style.color3,
                      fontSize: { xs: "0.88rem", md: ".95rem" },
                      lineHeight: 1.6,
                      maxWidth: 640,
                    }}
                  >
                    Next Steps:
                  </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                        gap: 1.25,
                      }}
                    >
                      {steps.map((step) => (
                        <Stack
                          key={step.title}
                          spacing={1}
                          sx={{
                            p: 1.35,
                            borderRadius: "5px",
                            backgroundColor: panelBg2,
                            border: "1px solid rgba(255,255,255,0.06)",
                            minHeight: 132,
                          }}
                        >
                          {step.icon}
                          <Typography
                            sx={{
                              color: style.color4,
                              fontWeight: 700,
                              lineHeight: 1.3,
                            }}
                          >
                            {step.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: style.color3,
                              fontSize: "0.88rem",
                              lineHeight: 1.55,
                            }}
                          >
                            {step.text}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                      sx={{ pt: 0.5 }}
                    >
                      <Button
                        component={RouterLink}
                        to="/my-gear"
                        startIcon={<DownloadRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 800,
                          px: 2.2,
                          py: 1,
                          color: style.color4,
                          backgroundColor: style.accentGreen || style.color2,
                          "&:hover": {
                            backgroundColor: style.accentGreen || style.color2,
                            filter: "brightness(1.05)",
                          },
                        }}
                      >
                        Go to My Gear
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/"
                        startIcon={<HomeRoundedIcon />}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          px: 2.2,
                          py: 1,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.09)",
                          },
                        }}
                      >
                        Return Home
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}